import PaginationButton from 'components/global/PaginationButton/PaginationButton';
import SearchInput from 'components/global/SearchInput';
import { useEffect } from 'react';
import { Col, Row, Table } from 'react-bootstrap';
import { getRows } from 'utils/pagination';
import LargeCheckBox from '../Constant/LargeCheckBox';
import { handleSimpleLenderSearchChange } from './lenderAccess.helpers';
import { lenderAccessSignal } from './lenderAccess.signal';
import loaderSignal from 'signals/Loader.signal';

const LenderAccess = ({
  lenderAccesses,
  selectedLenderIds = [],
  lenders,
  onLenderAccessUpdate,
  onSelectAllLenders,
  displayCheck = false,
}) => {
  const {
    lendersInView,
    lendersFilteredBySearch,
    page,
  } = lenderAccessSignal.value;
  const recordsPerPage = 8;
  const totalNumRecords = lendersFilteredBySearch.length;
  const totalNumOfPages = totalNumRecords > 0 ? Math.ceil(totalNumRecords / recordsPerPage) : 1;

  useEffect(() => {
    const lendersList = lenders || [];
    // Sort lenders: selected ones first, then alphabetically
    const sortedLenders = [...lendersList].sort((a, b) => {
      const aSelected = selectedLenderIds.includes(a.id);
      const bSelected = selectedLenderIds.includes(b.id);
      
      // If one is selected and the other isn't, selected comes first
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      
      // If both are selected or both are not selected, sort alphabetically
      return a.name.localeCompare(b.name);
    });
    
    lenderAccessSignal.update({ 
      lendersFilteredBySearch: sortedLenders,
      page: 0, // Reset to first page when lenders change
    });
  }, [lenders, selectedLenderIds]);

  // Calculate if all lenders are selected (for select all checkbox)
  const allLendersSelected = lenders && lenders.length > 0 && 
    lenders.every(lender => selectedLenderIds.includes(lender.id));
  const someLendersSelected = lenders && lenders.length > 0 && 
    lenders.some(lender => selectedLenderIds.includes(lender.id));

  useEffect(() => {
    const newLendersInView = getRows(
      lendersFilteredBySearch,
      page,
      recordsPerPage
    );
    lenderAccessSignal.update({ lendersInView: newLendersInView });
  }, [page, lendersFilteredBySearch]);


  return (
    <>
      <Row>
        <Col lg={6}>
          <SearchInput
            onChange={(e) =>
              handleSimpleLenderSearchChange(e, lenders || [], selectedLenderIds)
            }
          />
        </Col>
      </Row>
      {(!lenders || lenders.length === 0) && (
        <Row>
          <Col>
            <p className="text-muted">No lenders available. Lenders must be enrolled in at least one funding program for this EDO.</p>
          </Col>
        </Row>
      )}
      <Row>
        <Col lg={12}>
          <Table>
          <thead>
            <tr>
              <th>Lender</th>
              <th align="right" style={{ textAlign: 'right' }}>
                <div className="d-flex flex-column align-items-end">
                  <div className="mb-1">Access</div>
                  {onSelectAllLenders && lenders && lenders.length > 0 && (
                    <div className="d-flex align-items-center" style={{ fontSize: '0.875rem' }}>
                      <span className="mr-2">Select All</span>
                      <LargeCheckBox
                        name="select-all-lenders"
                        checked={allLendersSelected}
                        size="sm"
                        onClick={() => onSelectAllLenders(!allLendersSelected, lenders, lenderAccesses || [])}
                        disabled={loaderSignal.value.isContentLoading}
                      />
                    </div>
                  )}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {!displayCheck &&
              (lendersInView || []).map((lender) => {
                const lenderAccess = lenderAccesses?.find(
                  ({ lenderId }) =>
                    lenderId === lender.id
                );
                const isSelected = selectedLenderIds.includes(lender.id);
                return (
                  <tr key={lender.id}>
                    <td style={{ verticalAlign: 'middle' }}>
                      <p className="mb-0">{lender.name}</p>
                    </td>
                    <td align="right" style={{ verticalAlign: 'middle' }}>
                      <div className="d-flex justify-content-end">
                        <LargeCheckBox
                          name={`user-lender-access-lender-${lender.id}`}
                          checked={isSelected}
                          size="sm"
                          value={lender.id}
                          onClick={() => onLenderAccessUpdate({
                            lender,
                            lenderAccess,
                            isSelected,
                          })}
                          disabled={loaderSignal.value.isContentLoading}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            {displayCheck &&
              (lendersInView || []).map((lender) => {
                const checked = lenderAccesses?.find(
                  (la) => la.lenderId === lender.id
                );
                return (
                  <tr key={lender.id}>
                    <td style={{ verticalAlign: 'middle' }}>
                      <p className="mb-0">{lender.name}</p>
                    </td>
                    <td align="right" style={{ verticalAlign: 'middle' }}>
                      <div className="d-flex justify-content-end">
                        <LargeCheckBox
                          name={`user-lender-access-lender-${lender.id}`}
                          checked={checked}
                          size="sm"
                          value={lender.id}
                          onClick={() => onLenderAccessUpdate(lender.id)}
                          disabled={false}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </Table>
        </Col>
      </Row>
      <Row className="text-center">
        <Col>
          <PaginationButton
            increment={false}
            onClick={() => lenderAccessSignal.update({ page: page - 1 })}
            hidden={page <= 0}
          />
        </Col>
        <Col>
          <p>
            {page + 1}/{totalNumOfPages}
          </p>
        </Col>
        <Col>
          <PaginationButton
            increment
            onClick={() => lenderAccessSignal.update({ page: page + 1 })}
            hidden={page >= totalNumOfPages - 1}
          />
        </Col>
      </Row>
    </>
  );
};

export default LenderAccess;

